import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, MessageSquare, Mic2, Trophy, Loader2, ArrowRight, Check, X } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import client, { formatApiError } from "@/lib/api";
import { unlockAudio } from "@/lib/audio";
import { useLang } from "@/context/LangContext";
import SpeechCheck from "@/components/SpeechCheck";

const TYPES = [
  { kind: "Vocabulary", mins: 3, icon: Sparkles, c: "from-violet-500 to-fuchsia-500", desc: "Words you're learning" },
  { kind: "AI Conversation", mins: 5, icon: MessageSquare, c: "from-blue-500 to-cyan-500", desc: "A quick roleplay", convo: true },
  { kind: "Pronunciation", mins: 2, icon: Mic2, c: "from-pink-500 to-rose-500", desc: "Sharpen your accent" },
  { kind: "Real-Life Challenge", mins: 5, icon: Trophy, c: "from-amber-500 to-orange-500", desc: "Put it all together" },
];

export default function Practice() {
  const nav = useNavigate();
  const { t } = useLang();
  const [situations, setSituations] = useState([]);
  const [drill, setDrill] = useState(null);
  const [idx, setIdx] = useState(0);
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { client.get("/situations").then(({ data }) => setSituations(data)).catch(() => {}); }, []);

  const start = async (t) => {
    if (t.convo) { unlockAudio(); const s = situations[Math.floor(Math.random() * Math.max(1, situations.length))]; nav(`/speaking/${s?.id || "smalltalk"}`); return; }
    setLoading(true);
    try { const { data } = await client.post("/practice", { kind: t.kind }); setDrill({ ...data, kind: t.kind }); setIdx(0); setReveal(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  if (drill) {
    const item = drill.items[idx]; const last = idx >= drill.items.length - 1;
    return (
      <MobileShell nav={false}>
        <div className="px-5 py-6 flex flex-col min-h-screen">
          <header className="flex items-center justify-between mb-6">
            <button onClick={() => setDrill(null)} data-testid="drill-close" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 press"><X size={20} /></button>
            <p className="font-heading font-bold text-white">{drill.title}</p>
            <span className="text-sm text-slate-400">{idx + 1}/{drill.items.length}</span>
          </header>
          <div className="h-2 bg-white/8 rounded-full overflow-hidden mb-8"><div className="h-full mel-gradient transition-all" style={{ width: `${((idx + 1) / drill.items.length) * 100}%` }} /></div>
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <p className="text-slate-400 text-sm font-medium">{t("say_this")}</p>
            <p className="font-heading text-2xl font-extrabold text-white mt-3">{item?.prompt}</p>
            {reveal ? (
              <div className="mt-8 animate-float-up w-full">
                <p className="text-3xl font-heading font-extrabold mel-text-gradient">{item?.answer}</p>
                <p className="text-slate-400 mt-2">{item?.translation}</p>
                <SpeechCheck phrase={item?.answer} translation={item?.translation} testid="drill-speech" />
              </div>
            ) : <button onClick={() => setReveal(true)} data-testid="drill-reveal" className="mt-8 text-cyan-300 font-semibold underline">{t("show_answer")}</button>}
          </div>
          <button onClick={() => { if (last) { setDrill(null); toast.success(t("practice_complete") + " 🎉"); } else { setIdx(idx + 1); setReveal(false); } }} data-testid="drill-next"
            className="w-full mel-gradient text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(37,99,235,0.8)] press">
            {last ? t("finish") : t("next")} {last ? <Check size={18} /> : <ArrowRight size={18} />}
          </button>
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <div className="px-5 py-6">
        <h1 className="font-heading text-2xl font-extrabold text-white">{t("practice_title")}</h1>
        <p className="text-slate-400 mt-1">{t("practice_sub2")}.</p>
        {loading && <div className="mt-4 flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Building your session…</div>}
        <div className="grid grid-cols-1 gap-3 mt-5">
          {TYPES.map((t) => (
            <button key={t.kind} onClick={() => start(t)} disabled={loading} data-testid={`practice-${t.kind.replace(/\s+/g, "-").toLowerCase()}`}
              className="flex items-center gap-4 glass rounded-2xl p-4 text-left press disabled:opacity-60">
              <span className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.c} flex items-center justify-center text-white shrink-0`}><t.icon size={24} /></span>
              <span className="flex-1"><span className="block font-heading font-bold text-white">{t.mins} min {t.kind}</span><span className="text-sm text-slate-400">{t.desc}</span></span>
              <ArrowRight className="text-slate-500" />
            </button>
          ))}
        </div>
      </div>
    </MobileShell>
  );
}
